import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"

// "Сделать копию" button in the public sandbox editor.
//
// Signed-in visitor → POST /projects JSON with {name, state} seeded from the
// shared project → redirect to /projects/:id/edit. Reuses the same
// single-round-trip path that draft_migration_controller uses, so the
// behavior on failure is identical.
//
// Anonymous visitor → serialise the current editor store into
// `plot-planner:draft-v1` (same key the anon /editor autosave writes to) and
// redirect to /editor. The anonymous editor reads this key on mount via
// autosave_controller.#restoreFromLocalStorage, so the visitor picks up
// right where the shared plan left off and can keep editing without signup.
// If they later want to save to the cloud, the draft-migration banner on
// /projects (post-signup) picks up the same localStorage key.
const ANON_KEY = "plot-planner:draft-v1"

export default class extends Controller {
  static values = {
    signedIn: Boolean,
    name: String,
  }
  static targets = ["button"]

  async run() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.textContent = "Создаём копию…"
    }

    const state = this.#buildState()
    const name = `${this.nameValue || "Участок"} (копия)`

    if (this.signedInValue) {
      await this.#copyToServer({ name, state })
    } else {
      this.#copyToLocal({ state })
    }
  }

  async #copyToServer({ name, state }) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch("/projects", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({ project: { name, state } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!data.id) throw new Error("no id")
      window.location.href = `/projects/${data.id}/edit`
    } catch (e) {
      this.#restoreButton()
      alert("Не удалось создать копию. Попробуйте ещё раз.")
    }
  }

  #copyToLocal({ state }) {
    try {
      const payload = { version: 1, savedAt: Date.now(), ...state }
      localStorage.setItem(ANON_KEY, JSON.stringify(payload))
    } catch (e) {
      this.#restoreButton()
      alert("Не удалось сохранить черновик. Проверьте настройки приватности браузера.")
      return
    }
    window.location.href = "/editor"
  }

  #buildState() {
    return {
      plot: editorStore.plot,
      objects: editorStore.objects,
      norms: {
        ruleSetLocale: editorStore.norms.ruleSetLocale,
        ruleSetVersion: editorStore.norms.ruleSetVersion,
        disabledRuleIds: [...editorStore.norms.disabledRuleIds],
      },
      ui: {
        showEdgeDimensions: editorStore.ui.showEdgeDimensions,
        satelliteBg: {
          enabled: editorStore.ui.satelliteBg.enabled,
          opacity: editorStore.ui.satelliteBg.opacity,
        },
      },
    }
  }

  #restoreButton() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = false
    this.buttonTarget.textContent = "Сделать копию"
  }
}
